import React, { useState, useEffect, useCallback } from 'react';
import {
  DollarSign,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Calendar,
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  ScanLine,
} from 'lucide-react';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { apiEndpoints } from '../../utils/api';
import { Spinner } from '../../design-system';

// Types
interface OrganizationProfitSummary {
  organization_id: string;
  organization_name: string | null;
  owner_email: string | null;
  subscription_tier: string | null;
  revenue_usd: number;
  subscription_revenue_usd: number;
  topup_revenue_usd: number;
  llm_cost_usd: number;
  gross_profit_usd: number;
  margin_percent: number | null;
  scan_count: number;
  fix_count: number;
  request_count: number;
}

interface OrganizationProfitTotals {
  total_revenue_usd: number;
  total_llm_cost_usd: number;
  total_gross_profit_usd: number;
  overall_margin_percent: number | null;
}

interface OrganizationProfitResponse {
  items: OrganizationProfitSummary[];
  total: number;
  totals: OrganizationProfitTotals;
}

interface BillingPeriodOption {
  period_start: string;
  period_end: string;
  label: string;
}

interface ScanCostSummary {
  scan_id: string;
  scan_type: string | null;
  repository_name: string | null;
  total_cost_usd: number;
  total_tokens: number;
  input_tokens: number;
  output_tokens: number;
  request_count: number;
  created_at: string | null;
  user_id: string | null;
}

interface ScanCostsResponse {
  items: ScanCostSummary[];
  total: number;
  total_cost_usd: number;
}

type DateRange = '30d' | '90d' | 'all' | 'custom';

const OrganizationCostsSection: React.FC = () => {
  const authenticatedApi = useAuthenticatedApi();

  // State
  const [dateRange, setDateRange] = useState<DateRange>('30d');
  const [hideInactive, setHideInactive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Summary view state
  const [orgData, setOrgData] = useState<OrganizationProfitResponse | null>(null);
  const [orgPage, setOrgPage] = useState(0);

  // Billing periods
  const [billingPeriods, setBillingPeriods] = useState<BillingPeriodOption[]>([]);
  const [selectedPeriodIdx, setSelectedPeriodIdx] = useState<number>(-1);

  // Drill-down state
  const [selectedOrg, setSelectedOrg] = useState<OrganizationProfitSummary | null>(null);
  const [scanData, setScanData] = useState<ScanCostsResponse | null>(null);
  const [scanPage, setScanPage] = useState(0);
  const [scanLoading, setScanLoading] = useState(false);

  const pageSize = 25;

  // Calculate date range
  const getDateRange = useCallback((): { start_date?: string; end_date?: string } => {
    if (dateRange === 'custom' && selectedPeriodIdx >= 0 && billingPeriods[selectedPeriodIdx]) {
      return {
        start_date: billingPeriods[selectedPeriodIdx].period_start,
        end_date: billingPeriods[selectedPeriodIdx].period_end,
      };
    }

    if (dateRange === 'all' || dateRange === 'custom') return {};

    const end = new Date();
    const start = new Date();
    switch (dateRange) {
      case '30d':
        start.setDate(start.getDate() - 30);
        break;
      case '90d':
        start.setDate(start.getDate() - 90);
        break;
    }
    return {
      start_date: start.toISOString(),
      end_date: end.toISOString(),
    };
  }, [dateRange, selectedPeriodIdx, billingPeriods]);

  // Fetch billing periods
  const fetchBillingPeriods = useCallback(async () => {
    if (!authenticatedApi) return;
    try {
      const response = await authenticatedApi.get(
        apiEndpoints.admin.organizationCostsBillingPeriods
      );
      setBillingPeriods(response);
    } catch {
      // Non-critical, period selector just won't populate
    }
  }, [authenticatedApi]);

  // Fetch org profitability data
  const fetchOrgCosts = useCallback(async () => {
    if (!authenticatedApi) return;
    setLoading(true);
    setError(null);

    try {
      const { start_date, end_date } = getDateRange();
      const params = new URLSearchParams({
        skip: (orgPage * pageSize).toString(),
        limit: pageSize.toString(),
        hide_inactive: hideInactive.toString(),
      });
      if (start_date) params.append('start_date', start_date);
      if (end_date) params.append('end_date', end_date);

      const response = await authenticatedApi.get(
        `${apiEndpoints.admin.organizationCosts}?${params}`
      );
      setOrgData(response);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch organization costs');
    } finally {
      setLoading(false);
    }
  }, [authenticatedApi, orgPage, hideInactive, getDateRange]);

  // Fetch scan drill-down data
  const fetchScanCosts = useCallback(async () => {
    if (!authenticatedApi || !selectedOrg) return;
    setScanLoading(true);

    try {
      const { start_date, end_date } = getDateRange();
      const params = new URLSearchParams({
        skip: (scanPage * pageSize).toString(),
        limit: pageSize.toString(),
      });
      if (start_date) params.append('start_date', start_date);
      if (end_date) params.append('end_date', end_date);

      const response = await authenticatedApi.get(
        `${apiEndpoints.admin.organizationCostsScans(selectedOrg.organization_id)}?${params}`
      );
      setScanData(response);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch scan costs');
    } finally {
      setScanLoading(false);
    }
  }, [authenticatedApi, selectedOrg, scanPage, getDateRange]);

  // Load billing periods on mount
  useEffect(() => {
    fetchBillingPeriods();
  }, [fetchBillingPeriods]);

  // Load org data when not in drill-down
  useEffect(() => {
    if (!selectedOrg) {
      fetchOrgCosts();
    }
  }, [selectedOrg, fetchOrgCosts]);

  // Load scan data when in drill-down
  useEffect(() => {
    if (selectedOrg) {
      fetchScanCosts();
    }
  }, [selectedOrg, fetchScanCosts]);

  // Reset pages when date range changes
  useEffect(() => {
    setOrgPage(0);
    setScanPage(0);
  }, [dateRange, selectedPeriodIdx, hideInactive]);

  // Format currency
  const formatCurrency = (value: number): string => {
    if (Math.abs(value) >= 1) {
      return `$${value.toFixed(2)}`;
    }
    return `$${value.toFixed(4)}`;
  };

  // Format number with commas
  const formatNumber = (value: number): string => {
    return value.toLocaleString();
  };

  // Format date
  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Truncate ID
  const truncateId = (id: string, maxLength = 12): string => {
    if (id.length <= maxLength) return id;
    return `${id.slice(0, maxLength)}...`;
  };

  // Margin color
  const getMarginColor = (margin: number | null): string => {
    if (margin === null) return 'text-ui-text-tertiary';
    if (margin >= 0) return 'text-green-600 dark:text-green-400';
    return 'text-red-600 dark:text-red-400';
  };

  // Handle drill-down
  const handleOrgClick = (org: OrganizationProfitSummary) => {
    setSelectedOrg(org);
    setScanPage(0);
    setScanData(null);
  };

  const handleBackToSummary = () => {
    setSelectedOrg(null);
    setScanData(null);
    setScanPage(0);
  };

  // Pagination helpers
  const orgTotalPages = orgData ? Math.ceil(orgData.total / pageSize) : 0;
  const scanTotalPages = scanData ? Math.ceil(scanData.total / pageSize) : 0;

  // Period selector component
  const PeriodSelector = () => (
    <div className="flex items-center gap-2 flex-wrap">
      <Calendar className="w-4 h-4 text-ui-text-tertiary" />
      <select
        value={dateRange === 'custom' ? 'custom' : dateRange}
        onChange={(e) => {
          const val = e.target.value;
          if (val === 'custom') {
            setDateRange('custom');
            if (selectedPeriodIdx < 0 && billingPeriods.length > 0) {
              setSelectedPeriodIdx(0);
            }
          } else {
            setDateRange(val as DateRange);
          }
        }}
        className="px-3 py-1.5 text-sm bg-ui-background-secondary border border-ui-border-primary rounded-lg focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
      >
        <option value="30d">Last 30 days</option>
        <option value="90d">Last 90 days</option>
        <option value="all">All time</option>
        {billingPeriods.length > 0 && <option value="custom">Billing period...</option>}
      </select>
      {dateRange === 'custom' && billingPeriods.length > 0 && (
        <select
          value={selectedPeriodIdx}
          onChange={(e) => setSelectedPeriodIdx(Number(e.target.value))}
          className="px-3 py-1.5 text-sm bg-ui-background-secondary border border-ui-border-primary rounded-lg focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
        >
          {billingPeriods.map((period, idx) => (
            <option key={idx} value={idx}>
              {period.label}
            </option>
          ))}
        </select>
      )}
    </div>
  );

  // Render drill-down view
  if (selectedOrg) {
    return (
      <div className="p-4 sm:p-6">
        <div className="space-y-6">
          {/* Back button + Org header */}
          <div>
            <button
              onClick={handleBackToSummary}
              className="flex items-center gap-2 text-sm text-ui-text-secondary hover:text-ui-text-primary transition-colors mb-4"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to all organizations
            </button>

            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h2 className="text-xl font-semibold text-ui-text-primary">
                  {selectedOrg.organization_name || truncateId(selectedOrg.organization_id)}
                </h2>
                {selectedOrg.owner_email && (
                  <p className="text-sm text-ui-text-tertiary mt-0.5">
                    {selectedOrg.owner_email}
                  </p>
                )}
                <p className="text-sm text-ui-text-tertiary mt-1">
                  {selectedOrg.subscription_tier
                    ? `${selectedOrg.subscription_tier} tier`
                    : 'No subscription'}
                  {' '}&middot; Cost breakdown
                </p>
              </div>
              <PeriodSelector />
            </div>
          </div>

          {/* Org summary cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl p-4">
              <div className="text-sm font-medium text-ui-text-secondary">Revenue</div>
              <div className="text-xl font-bold text-ui-text-primary mt-1">
                {formatCurrency(selectedOrg.revenue_usd)}
              </div>
            </div>
            <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl p-4">
              <div className="text-sm font-medium text-ui-text-secondary">LLM Cost</div>
              <div className="text-xl font-bold text-ui-text-primary mt-1">
                {formatCurrency(selectedOrg.llm_cost_usd)}
              </div>
            </div>
            <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl p-4">
              <div className="text-sm font-medium text-ui-text-secondary">Gross Profit</div>
              <div className={`text-xl font-bold mt-1 ${getMarginColor(selectedOrg.gross_profit_usd >= 0 ? 1 : -1)}`}>
                {formatCurrency(selectedOrg.gross_profit_usd)}
              </div>
            </div>
            <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl p-4">
              <div className="text-sm font-medium text-ui-text-secondary">Margin</div>
              <div className={`text-xl font-bold mt-1 ${getMarginColor(selectedOrg.margin_percent)}`}>
                {selectedOrg.margin_percent !== null ? `${selectedOrg.margin_percent}%` : '-'}
              </div>
            </div>
          </div>

          {/* Scan costs table */}
          {scanLoading && (
            <div className="flex items-center justify-center py-12">
              <Spinner size={32} />
            </div>
          )}

          {!scanLoading && scanData && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-ui-background-secondary">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-ui-text-tertiary uppercase tracking-wider">
                      ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-ui-text-tertiary uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-ui-text-tertiary uppercase tracking-wider">
                      Repository
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-ui-text-tertiary uppercase tracking-wider">
                      Cost
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-ui-text-tertiary uppercase tracking-wider">
                      Tokens
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-ui-text-tertiary uppercase tracking-wider">
                      Requests
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-ui-text-tertiary uppercase tracking-wider">
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-ui-background-secondary divide-y divide-ui-border-primary">
                  {scanData.items.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-ui-text-tertiary">
                        No scan costs found for this organization in this period
                      </td>
                    </tr>
                  ) : (
                    scanData.items.map((scan) => (
                      <tr key={scan.scan_id} className="hover:bg-ui-background-tertiary">
                        <td className="px-4 py-3 text-sm font-mono text-ui-text-primary">
                          {truncateId(scan.scan_id)}
                        </td>
                        <td className="px-4 py-3 text-sm text-ui-text-secondary">
                          {scan.scan_type || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-ui-text-secondary">
                          {scan.repository_name || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-medium text-ui-text-primary">
                          {formatCurrency(scan.total_cost_usd)}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-ui-text-secondary">
                          {formatNumber(scan.total_tokens)}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-ui-text-secondary">
                          {scan.request_count}
                        </td>
                        <td className="px-4 py-3 text-sm text-ui-text-secondary">
                          {formatDate(scan.created_at)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Scan pagination */}
          {scanData && scanData.total > pageSize && (
            <div className="flex items-center justify-between px-4 py-3 bg-ui-background-secondary border-t border-ui-border-primary rounded-b-lg">
              <div className="text-sm text-ui-text-tertiary">
                Showing {scanPage * pageSize + 1} to{' '}
                {Math.min((scanPage + 1) * pageSize, scanData.total)} of{' '}
                {formatNumber(scanData.total)} items
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setScanPage(Math.max(0, scanPage - 1))}
                  disabled={scanPage === 0}
                  className="p-2 rounded-lg border border-ui-border-primary disabled:opacity-50 disabled:cursor-not-allowed hover:bg-ui-background-tertiary transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm text-ui-text-secondary">
                  Page {scanPage + 1} of {scanTotalPages}
                </span>
                <button
                  onClick={() => setScanPage(Math.min(scanTotalPages - 1, scanPage + 1))}
                  disabled={scanPage >= scanTotalPages - 1}
                  className="p-2 rounded-lg border border-ui-border-primary disabled:opacity-50 disabled:cursor-not-allowed hover:bg-ui-background-tertiary transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Render summary view
  return (
    <div className="p-4 sm:p-6">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-xl font-semibold text-ui-text-primary">
              Organization Costs
            </h2>
            <p className="text-sm text-ui-text-tertiary mt-1">
              Revenue, LLM costs, and profitability per organization
            </p>
          </div>
          <div className="flex items-center gap-3">
            <PeriodSelector />
            <label className="flex items-center gap-2 text-sm text-ui-text-secondary cursor-pointer select-none">
              <input
                type="checkbox"
                checked={hideInactive}
                onChange={(e) => setHideInactive(e.target.checked)}
                className="rounded border-ui-border-primary"
              />
              Hide inactive
            </label>
            <button
              onClick={fetchOrgCosts}
              className="flex items-center gap-2 px-3 py-2 text-sm text-ui-text-secondary hover:text-ui-text-primary transition-colors"
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Summary cards */}
        {orgData && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl p-4">
              <div className="flex items-center gap-2 mb-1">
                <DollarSign className="w-4 h-4 text-brand-primary" />
                <span className="text-sm font-medium text-ui-text-secondary">Total Revenue</span>
              </div>
              <div className="text-2xl font-bold text-ui-text-primary">
                {formatCurrency(orgData.totals.total_revenue_usd)}
              </div>
            </div>
            <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl p-4">
              <div className="flex items-center gap-2 mb-1">
                <ScanLine className="w-4 h-4 text-orange-500" />
                <span className="text-sm font-medium text-ui-text-secondary">Total LLM Cost</span>
              </div>
              <div className="text-2xl font-bold text-ui-text-primary">
                {formatCurrency(orgData.totals.total_llm_cost_usd)}
              </div>
            </div>
            <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl p-4">
              <div className="flex items-center gap-2 mb-1">
                {orgData.totals.total_gross_profit_usd >= 0 ? (
                  <TrendingUp className="w-4 h-4 text-green-500" />
                ) : (
                  <TrendingDown className="w-4 h-4 text-red-500" />
                )}
                <span className="text-sm font-medium text-ui-text-secondary">Gross Profit</span>
              </div>
              <div className={`text-2xl font-bold ${getMarginColor(orgData.totals.total_gross_profit_usd >= 0 ? 1 : -1)}`}>
                {formatCurrency(orgData.totals.total_gross_profit_usd)}
              </div>
            </div>
            <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl p-4">
              <div className="flex items-center gap-2 mb-1">
                <DollarSign className="w-4 h-4 text-blue-500" />
                <span className="text-sm font-medium text-ui-text-secondary">Overall Margin</span>
              </div>
              <div className={`text-2xl font-bold ${getMarginColor(orgData.totals.overall_margin_percent)}`}>
                {orgData.totals.overall_margin_percent !== null
                  ? `${orgData.totals.overall_margin_percent}%`
                  : '-'}
              </div>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="flex items-center gap-2 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Spinner size={32} />
          </div>
        )}

        {/* Organization table */}
        {!loading && orgData && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-ui-background-secondary">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ui-text-tertiary uppercase tracking-wider">
                    Organization
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ui-text-tertiary uppercase tracking-wider">
                    Tier
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-ui-text-tertiary uppercase tracking-wider">
                    Revenue
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-ui-text-tertiary uppercase tracking-wider">
                    LLM Cost
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-ui-text-tertiary uppercase tracking-wider">
                    Gross Profit
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-ui-text-tertiary uppercase tracking-wider">
                    Margin
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-ui-text-tertiary uppercase tracking-wider">
                    Scans / Fixes
                  </th>
                </tr>
              </thead>
              <tbody className="bg-ui-background-secondary divide-y divide-ui-border-primary">
                {orgData.items.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-ui-text-tertiary">
                      No organization data found for this period
                    </td>
                  </tr>
                ) : (
                  orgData.items.map((org) => (
                    <tr
                      key={org.organization_id}
                      className="hover:bg-ui-background-tertiary cursor-pointer"
                      onClick={() => handleOrgClick(org)}
                    >
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-brand-primary hover:underline">
                          {org.organization_name || truncateId(org.organization_id)}
                        </div>
                        {org.owner_email && (
                          <div className="text-xs text-ui-text-tertiary">
                            {org.owner_email}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-ui-text-secondary">
                        {org.subscription_tier || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-ui-text-primary">
                        {formatCurrency(org.revenue_usd)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-ui-text-primary">
                        {formatCurrency(org.llm_cost_usd)}
                      </td>
                      <td className={`px-4 py-3 text-sm text-right font-medium ${getMarginColor(org.gross_profit_usd >= 0 ? 1 : -1)}`}>
                        {formatCurrency(org.gross_profit_usd)}
                      </td>
                      <td className={`px-4 py-3 text-sm text-right font-medium ${getMarginColor(org.margin_percent)}`}>
                        {org.margin_percent !== null ? `${org.margin_percent}%` : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-ui-text-secondary">
                        {org.scan_count} / {org.fix_count}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {orgData && orgData.total > pageSize && (
          <div className="flex items-center justify-between px-4 py-3 bg-ui-background-secondary border-t border-ui-border-primary rounded-b-lg">
            <div className="text-sm text-ui-text-tertiary">
              Showing {orgPage * pageSize + 1} to{' '}
              {Math.min((orgPage + 1) * pageSize, orgData.total)} of{' '}
              {formatNumber(orgData.total)} organizations
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setOrgPage(Math.max(0, orgPage - 1))}
                disabled={orgPage === 0}
                className="p-2 rounded-lg border border-ui-border-primary disabled:opacity-50 disabled:cursor-not-allowed hover:bg-ui-background-tertiary transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-ui-text-secondary">
                Page {orgPage + 1} of {orgTotalPages}
              </span>
              <button
                onClick={() => setOrgPage(Math.min(orgTotalPages - 1, orgPage + 1))}
                disabled={orgPage >= orgTotalPages - 1}
                className="p-2 rounded-lg border border-ui-border-primary disabled:opacity-50 disabled:cursor-not-allowed hover:bg-ui-background-tertiary transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrganizationCostsSection;
